import type { ComponentType } from 'react';

import { useUITheme } from '@crehana/compass.ui';
import { useTranslation } from '@crehana/i18n';
import FileUpload from '@crehana/icons/outline/FileUpload';
import Inbox from '@crehana/icons/pictograms/Inbox';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, Loader } from '@crehana/ui';
import { date } from '@crehana/utils';
import { clsx } from 'clsx';

import {
  Divider,
  FileHeaderWrapper,
  Notification,
  StatusWrapper,
  TableWrapper,
  WrapperImport,
} from './ImportList.styles';

const { baseLighter, grayMain, blueLighter } = colors;

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

type TNullableBaseData = Nullable<{
  fileName: string;
  createdAt: string;
  user: Nullable<{ fullName: string; email: string }>;
}>;

interface IBaseData extends TNullableBaseData {
  originalId: string;
}

type Stringify<T> = Record<keyof T, string>;

type TFileHeaders<T> = Partial<Stringify<Omit<T, 'originalId'>>>;

interface IImportList<T extends IBaseData> {
  headers: TFileHeaders<T>;
  isLoading: boolean;
  totalFiles?: number;
  files?: T[];
  action?: ComponentType<{ item: T }>;
  title: string;
  notificationLabel?: string;
}

function ImportList<TData extends IBaseData>({
  title,
  notificationLabel,
  headers,
  isLoading,
  files,
  totalFiles,
  action: Action,
}: IImportList<TData>) {
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const textColor = `text-${isDark ? 'white' : 'base-dark'}`;

  const tableBody = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!files?.length) return <></>;

    return (
      <tbody>
        {files.map((item: TData) => {
          const { fileName, user, createdAt, originalId, ...rest } = item;
          return (
            <tr key={`record-${originalId}`}>
              <td className="font-body4">{fileName}</td>
              {Object.keys(rest)
                .filter(key => Object.hasOwn(headers, key))
                .map(key => {
                  const value = String(rest[key as keyof typeof rest]);
                  return (
                    <td key={`${key}-${value}`} className="font-body4">
                      {value}
                    </td>
                  );
                })}
              <td className="font-body4">
                {user?.fullName}
                <br />
                <span className="font-legals text-gray-dark">
                  {user?.email ? `(${user.email})` : ''}
                </span>
              </td>
              <td className="font-body4">
                {createdAt && date.format(createdAt, 'DD/MM/YYYY HH:MM')}
              </td>
              <td>{!!Action && <Action item={item} />}</td>
            </tr>
          );
        })}
      </tbody>
    );
  };

  const emptyList = () => (
    <StatusWrapper isDark={isDark}>
      <Inbox color={isDark ? blueLighter : grayMain} size={48} />
      <p className="font-body4">{t('IMPORT_LIST_EMPTY_LIST')}</p>
    </StatusWrapper>
  );

  const loader = () => (
    <StatusWrapper isDark={isDark}>
      <Loader isWhite={isDark} />
    </StatusWrapper>
  );

  return (
    <WrapperImport isDark={isDark}>
      <FileHeaderWrapper>
        <div className="flex items-center">
          <FileUpload
            color={isDark ? baseLighter : grayMain}
            size={24}
            className="mr-8"
          />
          <p className={`font-subtitle3 mr-8 ${textColor}`}>{title}</p>
          <Notification className="font-caption" isDark={isDark}>
            {totalFiles || 0}
          </Notification>
        </div>
        {files?.length && notificationLabel ? (
          <p className="font-legals text-gray-dark">{notificationLabel}</p>
        ) : null}
      </FileHeaderWrapper>
      <Divider isDark={isDark} />
      <TableWrapper isDark={isDark}>
        <thead>
          <tr>
            {Object.keys(headers).map((header, i) => (
              <th
                key={`header-${header}`}
                className={clsx(
                  'font-label text-gray-dark',
                  `${i === 1 ? 'w-160' : ''}`,
                )}
              >
                {headers[header as keyof TFileHeaders<TData>]}
              </th>
            ))}
          </tr>
        </thead>
        {tableBody()}
      </TableWrapper>
      {isLoading ? loader() : !totalFiles && emptyList()}
    </WrapperImport>
  );
}

export default ImportList;
